// Modules
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';

import { BILLING_ROLES, DEPARTMENTS } from 'constants';
import {
  CheckboxInput,
  DateInput,
  IntegerInput,
  MoneyInput,
  RadioInput,
  SelectInput,
  TextInput
} from 'components/forms/form-elements';
import { CompanySelect } from 'components/forms/selects';
import { Form, Subform, SubformHeader } from 'components/forms/form-structure';
import { convertArrayToOptions } from 'utils/input-utils';
import { handleServerError } from 'utils/form-utils';
import MultipleFileUploadForm from
  'components/models/files/multiple-file-upload-form';
import requiredValidation from 'utils/required-validation';

const types = [
  { value: 'pt1', label: 'Ongoing PraeceTech One', department: 'Tech' },
  { value: 'ptbasic', label: 'Ongoing PraeceTech Basic', department: 'Tech' },
  { value: 'ptcore', label: 'Ongoing PraeceTech Core', department: 'Tech' },
  { value: 'ptprotect', label: 'Ongoing PraeceProtect', department: 'Tech' },
  { value: 'ptprotectplus', label: 'Ongoing PraeceProtect Plus', department: 'Tech' },
  { value: 'ptprotecthourly', label: 'Ongoing PraeceProtect Hourly', department: 'Tech' },
  { value: 'pfmaint', label: 'Ongoing PraeceFlow Maintenance', department: 'Flow' },
  { value: 'pthourly', label: 'Ongoing PraeceTech Hourly Consulting', department: 'Tech' },
  { value: 'pfhourly', label: 'Ongoing PraeceFlow Hourly Consulting', department: 'Flow' },
  { value: 'ptproject', label: 'PraeceTech Project', department: 'Tech' },
  { value: 'pfproject', label: 'PraeceFlow Project', department: 'Flow' },
  { value: 'ptsubproject', label: 'PraeceTech Subproject', department: 'Tech' },
  { value: 'pfsubproject', label: 'PraeceFlow Subproject', department: 'Flow' }
];

// Defaults
const rates = [
  { estimatedHours: 0, role: 'Tech', rate: 212 },
  { estimatedHours: 0, role: 'Developer', rate: 260 },
  { estimatedHours: 0, role: 'Team Lead', rate: 260 },
  { estimatedHours: 0, role: 'Consultant', rate: 329 },
  { estimatedHours: 0, role: 'Architect', rate: 329 }
];

const monthlyRates = [
  { estimatedHours: 0, role: 'Tech', rate: 212 },
  { estimatedHours: 0, role: 'Developer', rate: 260 },
  { estimatedHours: 0, role: 'Team Lead', rate: 260 },
  { estimatedHours: 0, role: 'Consultant', rate: 329 },
  { estimatedHours: 0, role: 'Architect', rate: 329 }
];

const monthlyProjectRates = [
  { role: 'Tech', rate: 212 },
  { role: 'Developer', rate: 260 },
  { role: 'Team Lead', rate: 260 },
  { role: 'Consultant', rate: 329 },
  { role: 'Architect', rate: 329 }
];

const typeValues = {
  pt1: () => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyDescription: 'PraeceTech One Monthly',
    nickname: 'PT One',
    title: 'Ongoing PraeceTech One',
    department: 'Technology'
  }),
  ptbasic: () => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyRate: 333,
    monthlyDescription: 'PraeceTech Basic Monthly',
    timeDefaultBillable: true,
    nickname: 'PT Basic',
    title: 'Ongoing PraeceTech Basic',
    department: 'Technology'
  }),
  ptcore: () => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyRate: 169,
    monthlyDescription: 'PraeceTech Core Monthly',
    timeDefaultBillable: true,
    nickname: 'PT Core',
    title: 'Ongoing PraeceTech Core',
    department: 'Technology'
  }),
  ptprotect: () => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyRate: 310,
    monthlyDescription: 'PraeceProtect Monthly',
    timeDefaultBillable: false,
    nickname: 'Protect',
    title: 'Ongoing PraeceProtect',
    department: 'Technology'
  }),
  ptprotectplus: () => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyRate: 480,
    monthlyDescription: 'PraeceProtect Plus Monthly',
    timeDefaultBillable: false,
    nickname: 'Protect Plus',
    title: 'Ongoing PraeceProtect Plus',
    department: 'Technology'
  }),
  ptprotecthourly: () => ({
    rates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyRate: 75,
    monthlyDescription: 'PraeceProtect Monthly',
    timeDefaultBillable: true,
    nickname: 'Protect Hourly',
    title: 'Ongoing PraeceProtect Hourly',
    department: 'Technology'
  }),
  pthourly: () => ({
    rates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: true,
    nickname: 'Tech Hourly',
    title: 'Ongoing PraeceTech Hourly Consulting',
    department: 'Technology'
  }),
  pfmaint: title => ({
    rates: monthlyRates,
    afterHoursPremium: 106,
    monthly: true,
    monthlyDescription: `PraeceFlow Maintenance${title ? ` - ${title}` : ''}`,
    timeDefaultBillable: false,
    nickname: `Flow Maint${title ? ` (${title})` : ''}`,
    title: `Ongoing PraeceFlow Maintenance${title ? ` - ${title}` : ''}`,
    department: 'Development'
  }),
  pfhourly: title => ({
    rates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: true,
    nickname: `Flow Hourly${title ? ` (${title})` : ''}`,
    title: `Ongoing PraeceFlow Hourly Consulting${title ? ` - ${title}` : ''}`,
    department: 'Development'
  }),
  ptproject: title => ({
    rates: monthlyProjectRates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: false,
    nickname: `Tech Project${title ? ` (${title})` : ''}`,
    title: `PraeceTech Project${title ? ` - ${title}` : ''}`,
    department: 'Technology'
  }),
  pfproject: title => ({
    rates: monthlyProjectRates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: false,
    nickname: `Flow Project${title ? ` (${title})` : ''}`,
    title: `PraeceFlow Project${title ? ` - ${title}` : ''}`,
    department: 'Development'
  }),
  ptsubproject: title => ({
    rates: monthlyProjectRates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: false,
    nickname: `Tech Subproject${title ? ` (${title})` : ''}`,
    title: `PraeceTech Subproject${title ? ` - ${title}` : ''}`,
    department: 'Technology'
  }),
  pfsubproject: title => ({
    rates: monthlyProjectRates,
    afterHoursPremium: 106,
    monthly: false,
    monthlyDescription: null,
    timeDefaultBillable: false,
    nickname: `Flow Subproject${title ? ` (${title})` : ''}`,
    title: `PraeceFlow Subproject${title ? ` - ${title}` : ''}`,
    department: 'Development'
  })
};

// Component
class AgreementForm extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    currentValues: PropTypes.object,
    initialValues: PropTypes.object,
    currentUser: PropTypes.object,
    change: PropTypes.func
  }

  constructor() {
    super();

    this.applyTemplate = this.applyTemplate.bind(this);
    this.template = () => setTimeout(this.applyTemplate, 0);
  }

  applyTemplate() {
    const { change, currentValues } = this.props;
    const { type, projectTitle } = currentValues || {};

    _.forEach(typeValues[type](projectTitle), (value, key) => change(key, value));
  }

  render() {
    const { currentValues, initialValues, change, ...props } = this.props;
    const { monthly, type } = currentValues || {};
    const hasTitle = _.includes(['ptproject', 'pfproject', 'pfhourly', 'pfmaint', 'ptsubproject', 'pfsubproject'], type);
    const editing = _.get(initialValues, 'id');

    const typeGroupBy = options => _.groupBy(options, 'department');

    return (
      <Form onSubmit={props.handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])}>
        {!editing &&
          <Subform>
            <SubformHeader>Select Template</SubformHeader>
            <div>
              <Field component={SelectInput} name="type" label="Template" options={types} groupBy={typeGroupBy} onChange={this.template} />
              {hasTitle && <Field component={TextInput} name="projectTitle" label="Project Title" placeholder="Describe Project..." onChange={this.template} />}
            </div>
          </Subform>
        }
        <Subform>
          <SubformHeader>Details</SubformHeader>
          <Field component={CompanySelect} name="company" label="Company" />
          <Field component={TextInput} name="title" label={`Title ${!editing ? '(autocompleted by template)' : ''}`} />
          <Field component={TextInput} name="nickname" label={`Nickname ${!editing ? '(autocompleted by template)' : ''}`} />
          <div className="form-flex-row">
            <Field component={DateInput} name="startDate" label="Start Date" />
            <Field component={DateInput} name="endDate" label="EndDate" />
          </div>
          <Field component={RadioInput} name="department" label="Department" options={convertArrayToOptions(DEPARTMENTS)} />
          <Field component={CheckboxInput} name="requiresWorkOrder" label="Invoice by Work Order" />
        </Subform>
        <Subform>
          <SubformHeader>Monthly Billing</SubformHeader>
          <Field component={CheckboxInput} name="monthly" label="Include Monthly Billing" />
          {monthly && <Field component={TextInput} name="monthlyRate" label="Monthly Rate" />}
          {monthly && <Field component={TextInput} name="monthlyDescription" label={`Monthly Description ${!editing ? '(autocompleted by template)' : ''}`} />}
        </Subform>
        <Subform className="hourly-rates">
          <SubformHeader>Hourly Rates</SubformHeader>
          <Field component={CheckboxInput} name="timeDefaultBillable" label="Time Reports are Billable by Default" />
          {_.map(BILLING_ROLES, role => {
            const index = _.findIndex(currentValues.rates, { role });
            const roleAddon = <span style={{ minWidth: '175px' }} className="input-group-text">{role}</span>;

            return <Field key={role} component={MoneyInput} name={`rates[${index}].rate`} addonRight={roleAddon} />;
          })}
          <Field component={MoneyInput} name="afterHoursPremium" addonRight={<span style={{ minWidth: '175px' }} className="input-group-text">After Hours Premium</span>} />
        </Subform>
        <Subform className="hourly-rates">
          <SubformHeader>Hours Estimates</SubformHeader>
          {_.map(BILLING_ROLES, role => {
            const index = _.findIndex(currentValues.rates, { role });
            const roleAddon = <span style={{ minWidth: '175px' }} className="input-group-text">{role}</span>;

            return <Field key={role} component={IntegerInput} name={`rates[${index}].estimatedHours`} addonRight={roleAddon} />;
          })}
        </Subform>
        <MultipleFileUploadForm change={change} name="signedAgreements" label="Signed Agreements" />
      </Form>
    );
  }
}

const ConnectedForm = connect((state, ownProps) => ({
  currentValues: formValueSelector(ownProps.form)(state, 'monthly', 'monthlyRate', 'rates', 'type', 'projectTitle', 'title', 'signedAgreements')
}))(AgreementForm);

export default reduxForm({
  form: 'agreement-form',
  validate: (values) => {
    const errors = {};
    const required = requiredValidation(['title', 'nickname', 'startDate', 'afterHoursPremium', 'department'], values);

    if (values.monthly) _.assign(required, requiredValidation(['monthlyDescription', 'monthlyRate'], values));

    const requiresTitle = _.includes(['ptproject', 'pfproject', 'pfmaint'], values.type);
    if (values.type && requiresTitle) _.assign(required, requiredValidation(['projectTitle'], values));

    errors.rates = _.map(values.rates, rate => requiredValidation(['rate', 'role', 'estimatedHours'], rate));

    const allErrors = _.assign(errors, required);

    return allErrors;
  },
  onSubmitFail: handleServerError
})(ConnectedForm);



// WEBPACK FOOTER //
// ./app/components/models/agreement/agreement-form.js